/**
 * Phone number validation and formatting utilities
 *
 * SESSION_200: Onboarding Step 2 - Contact Preferences
 * Handles US phone number validation and E.164 normalization
 */

/**
 * Normalizes a phone number to E.164 format (+12065551234)
 * Accepts common US formats: (206) 555-1234, 206-555-1234, +12065551234
 *
 * @param phone - The phone number to normalize
 * @returns E.164 formatted number or null if invalid
 */
export function normalizePhoneNumber(phone: string): string | null {
  if (!phone || !phone.trim()) {
    return null;
  }

  // Extract digits only
  const digits = phone.replace(/\D/g, '');

  // If already has country code (11 digits starting with 1), format as +1...
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }

  // If 10-digit US number, add +1 prefix
  if (digits.length === 10) {
    return `+1${digits}`;
  }

  // For other formats (7-digit local, international), return null as invalid
  // We only support US numbers for now
  if (digits.length < 10 || digits.length > 11) {
    return null;
  }

  return null;
}

/**
 * Validates if a phone number is valid
 * Accepts common formats: (555) 123-4567, 555-123-4567, 5551234567, +15551234567
 *
 * @param phone - The phone number to validate
 * @returns True if valid, false otherwise
 */
export function isValidPhoneNumber(phone: string | null | undefined): boolean {
  if (!phone || !phone.trim()) {
    return true; // Empty is valid (optional field)
  }

  // Extract digits only
  const digits = phone.replace(/\D/g, '');

  // Valid: 10-digit US, 11-digit with country code
  if (digits.length < 10 || digits.length > 11) {
    return false;
  }

  // If 11 digits, must start with 1 (US country code)
  if (digits.length === 11 && !digits.startsWith('1')) {
    return false;
  }

  // Reject all zeros
  if (/^0+$/.test(digits)) {
    return false;
  }

  // Reject all same digit (e.g., 1111111111)
  if (new Set(digits).size === 1) {
    return false;
  }

  return true;
}

/**
 * Formats an E.164 phone number for display
 * +12065551234 -> (206) 555-1234
 *
 * @param e164Phone - Phone number in E.164 format
 * @returns Formatted phone number for display
 */
export function formatPhoneForDisplay(e164Phone: string | null | undefined): string {
  if (!e164Phone) {
    return '';
  }

  // Extract digits only
  const digits = e164Phone.replace(/\D/g, '');

  // Handle US numbers (10 or 11 digits)
  if (digits.length === 11 && digits.startsWith('1')) {
    // Remove country code for display
    const number = digits.substring(1);
    return `(${number.substring(0, 3)}) ${number.substring(3, 6)}-${number.substring(6)}`;
  }

  if (digits.length === 10) {
    return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
  }

  // Return as-is for other formats
  return e164Phone;
}

/**
 * Formats phone number as user types (live formatting)
 * Types: 2065 -> (206) 5
 * Types: 2065551234 -> (206) 555-1234
 *
 * @param input - Raw user input
 * @returns Formatted phone number
 */
export function formatPhoneAsYouType(input: string): string {
  // Extract digits only
  const digits = input.replace(/\D/g, '');

  if (digits.length === 0) {
    return '';
  }

  if (digits.length <= 3) {
    return `(${digits}`;
  }

  if (digits.length <= 6) {
    return `(${digits.substring(0, 3)}) ${digits.substring(3)}`;
  }

  if (digits.length <= 10) {
    return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
  }

  // Max 10 digits for US number
  return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6, 10)}`;
}
